import { CheckCircleOutlined, CloseCircleOutlined, GiftOutlined } from '@ant-design/icons';
import { Button, Card, Modal, Space, Typography, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useOptionalI18n } from '../i18n';
import { apiService } from '../services/api';

const { Title, Text, Paragraph } = Typography;

interface Coupon {
  id: number;
  amount: number;
  description: string;
  expiresAt?: string;
  source: string;
  createdAt: string;
}

interface CouponNotificationModalProps {
  visible: boolean;
  onClose: () => void;
  userId?: number;
}

const CouponNotificationModal: React.FC<CouponNotificationModalProps> = ({
  visible,
  onClose,
  userId
}) => {
  const { t } = useOptionalI18n();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (visible && userId) {
      fetchUnreadCoupons();
    }
  }, [visible, userId]);

  const fetchUnreadCoupons = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUnreadCoupons();
      if (response.success && response.data) {
        setCoupons(response.data);
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('获取未读优惠券失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (coupons.length === 0) return;

    const currentCoupon = coupons[currentIndex];
    try {
      const response = await apiService.markCouponAsRead(currentCoupon.id);

      if (response.success) {
        // 移动到下一张优惠券或关闭
        if (currentIndex < coupons.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          message.success(t('messages.coupon.viewedAll'));
          onClose();
        }
      } else {
        message.error(response.message || t('messages.common.error'));
      }
    } catch (error) {
      console.error('标记优惠券为已读失败:', error);
      message.error(t('messages.common.error'));
    }
  };

  const handleClose = async () => {
    // 关闭前标记当前优惠券为已读
    if (coupons.length > 0 && currentIndex < coupons.length) {
      try {
        const currentCoupon = coupons[currentIndex];
        await apiService.markCouponAsRead(currentCoupon.id);
      } catch (error) {
        console.error('标记优惠券为已读失败:', error);
      }
    }
    onClose();
  };

  const getSourceText = (source: string) => {
    const sourceMap: { [key: string]: string } = {
      'ADMIN_GRANT': t('messages.coupon.source.adminGrant'),
      'SYSTEM_REWARD': t('messages.coupon.source.systemReward'),
      'REFERRAL_FIRST_PURCHASE': t('messages.coupon.source.referralFirst'),
      'REFERRAL_REGULAR_PURCHASE': t('messages.coupon.source.referralRegular')
    };
    return sourceMap[source] || source;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('messages.coupon.noExpiry');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return t('messages.coupon.noExpiry');
    }
  };

  if (coupons.length === 0) {
    return null;
  }

  const currentCoupon = coupons[currentIndex];

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={500}
      centered
      closable={false}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '16px' }}>
          <GiftOutlined />
        </div>

        <Title level={3} style={{ marginBottom: '8px' }}>
          {t('messages.coupon.modal.title')}
        </Title>

        <Text type="secondary" style={{ fontSize: '14px' }}>
          {coupons.length > 1 ? t('messages.coupon.modal.progress', { current: currentIndex + 1, total: coupons.length }) : ''}
        </Text>

        <Card
          style={{
            marginTop: '24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            color: 'white'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '8px' }}>
            NT$ {currentCoupon.amount}
          </div>

          <Paragraph style={{ color: 'white', marginBottom: '16px', fontSize: '16px' }}>
            {currentCoupon.description || t('messages.coupon.modal.defaultDescription')}
          </Paragraph>

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text style={{ color: 'rgba(255,255,255,0.8)' }}>{t('messages.coupon.modal.sourceLabel')}：</Text>
              <Text style={{ color: 'white' }}>{getSourceText(currentCoupon.source)}</Text>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text style={{ color: 'rgba(255,255,255,0.8)' }}>{t('messages.coupon.modal.expireLabel')}：</Text>
              <Text style={{ color: 'white' }}>{formatDate(currentCoupon.expiresAt)}</Text>
            </div>
          </Space>
        </Card>

        <Space style={{ marginTop: '24px', width: '100%', justifyContent: 'center' }} size="middle">
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={handleMarkAsRead}
            style={{ minWidth: '120px' }}
          >
            {currentIndex < coupons.length - 1 ? t('messages.coupon.modal.next') : t('messages.coupon.modal.done')}
          </Button>

          <Button
            size="large"
            icon={<CloseCircleOutlined />}
            onClick={handleClose}
            style={{ minWidth: '120px' }}
          >
            {t('common.close')}
          </Button>
        </Space>

        {coupons.length > 1 && (
          <Text type="secondary" style={{ fontSize: '12px', marginTop: '16px', display: 'block' }}>
            {t('messages.coupon.modal.remaining', { count: coupons.length - currentIndex - 1 })}
          </Text>
        )}
      </div>
    </Modal>
  );
};

export default CouponNotificationModal;
